import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, XCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizView = ({ userId }) => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuiz();
  }, [scenarioId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/quiz/${scenarioId}`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    
    const question = quiz.questions[currentQuestion];
    const isCorrect = selectedAnswer === question.correct;
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setAnswers([...answers, {
      question_id: question.id,
      selected: selectedAnswer,
      correct: question.correct,
      is_correct: isCorrect
    }]);
    
    setShowExplanation(true);
  };

  const handleNext = async () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setCompleted(true);
      
      try {
        await axios.post(`${API}/quiz/attempt`, {
          user_id: userId,
          scenario_id: scenarioId,
          score: score + (selectedAnswer === quiz.questions[currentQuestion].correct ? 1 : 0),
          total: quiz.questions.length,
          answers: [...answers, {
            question_id: quiz.questions[currentQuestion].id,
            selected: selectedAnswer,
            correct: quiz.questions[currentQuestion].correct,
            is_correct: selectedAnswer === quiz.questions[currentQuestion].correct
          }]
        });
        
        const finalScore = score + (selectedAnswer === quiz.questions[currentQuestion].correct ? 1 : 0);
        if (finalScore === quiz.questions.length) {
          toast.success('Perfect score! 🎉', {
            description: 'You answered all questions correctly!'
          });
        }
      } catch (error) {
        console.error('Error submitting quiz:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading quiz...</div>
      </div>
    );
  }

  if (!quiz) return null;

  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

  if (completed) {
    const finalScore = score + (selectedAnswer === quiz.questions[quiz.questions.length - 1].correct ? 1 : 0);
    const percentage = (finalScore / quiz.questions.length) * 100;
    
    return (
      <div className="min-h-screen app-container p-6 md:p-8 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white dark:bg-[#232129] rounded-3xl p-8 text-center"
        >
          <div className="mb-6">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-[#CCFF00] flex items-center justify-center">
              <span className="text-5xl font-extrabold text-black">{finalScore}/{quiz.questions.length}</span>
            </div>
            <h2 className="text-3xl font-bold mb-2 text-[#1A1A1A] dark:text-[#FFFFFE]">Quiz Complete!</h2>
            <p className="text-lg text-[#5F6C7B] dark:text-[#94A1B2]">
              You scored {percentage.toFixed(0)}%
            </p>
          </div>
          
          <div className="space-y-3 mb-8">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-[#CCFF00] text-black font-bold px-8 py-4 rounded-full hover:scale-105 transition-transform"
              data-testid="back-dashboard-button"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/resources')}
              variant="outline"
              className="w-full border-2 border-black dark:border-white font-bold px-8 py-4 rounded-full"
              data-testid="explore-resources-button"
            >
              Explore Resources
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-container p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="rounded-full"
            data-testid="back-button-quiz"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm font-medium text-[#5F6C7B] dark:text-[#94A1B2]">
            Question {currentQuestion + 1} of {quiz.questions.length}
          </span>
        </div>

        <Progress value={progress} className="mb-8 h-2" />

        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-[#232129] rounded-3xl p-8 mb-6"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#1A1A1A] dark:text-[#FFFFFE]">
            {question.question}
          </h2>

          <div className="space-y-4 mb-6">
            {question.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === question.correct;
              const showCorrect = showExplanation && isCorrect;
              const showWrong = showExplanation && isSelected && !isCorrect;

              return (
                <motion.button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showExplanation}
                  whileHover={{ scale: showExplanation ? 1 : 1.02 }}
                  whileTap={{ scale: showExplanation ? 1 : 0.98 }}
                  className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                    showCorrect
                      ? 'bg-[#CCFF00]/20 border-[#CCFF00]'
                      : showWrong
                      ? 'bg-red-500/20 border-red-500'
                      : isSelected
                      ? 'bg-[#CCFF00]/10 border-[#CCFF00]'
                      : 'border-black/10 dark:border-white/10 hover:border-[#CCFF00]'
                  }`}
                  data-testid={`quiz-option-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-[#1A1A1A] dark:text-[#FFFFFE]">{option}</span>
                    {showCorrect && <CheckCircle2 className="w-6 h-6 text-[#CCFF00]" />}
                    {showWrong && <XCircle className="w-6 h-6 text-red-500" />}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {showExplanation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 bg-[#CCFF00]/10 border-2 border-[#CCFF00] rounded-2xl mb-6"
            >
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-[#CCFF00] mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">Explanation</h4>
                  <p className="text-[#5F6C7B] dark:text-[#94A1B2]">{question.explanation}</p>
                </div>
              </div>
            </motion.div>
          )}

          <Button
            onClick={showExplanation ? handleNext : handleSubmitAnswer}
            disabled={selectedAnswer === null}
            className="w-full bg-[#CCFF00] text-black font-bold px-8 py-4 rounded-full hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            data-testid="quiz-submit-button"
          >
            {showExplanation ? (currentQuestion < quiz.questions.length - 1 ? 'Next Question' : 'Finish Quiz') : 'Submit Answer'}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default QuizView;
